import { BrandForm } from '@/components/compliance';
import { ComplianceLayout, useComplianceLayout } from '@/components/compliance/compliance-layout';
import { ComplianceSkeleton } from '@/components/compliance/compliance-skeleton';
import { useCreateDraftBrand } from '@/components/compliance/hooks/use-create-draft-brand';
import { useDeleteBrand } from '@/components/compliance/hooks/use-delete-brand';
import { useSubmitBrand } from '@/components/compliance/hooks/use-submit-brand';
import { useUpdateBrandDraft } from '@/components/compliance/hooks/use-update-brand-draft';
import { SoleProprietorOtpAlert } from '@/components/compliance/sole-proprietor-otp-alert';
import { SoleProprietorOtpDialog } from '@/components/compliance/sole-proprietor-otp-dialog';
import { StepIntro } from '@/components/compliance/step-intro';
import type { BrandWithCampaignsResponse, UpdateBrandRequest } from '@/components/compliance/types';
import { StripeElements } from '@/components/payment/stripe-elements';
import { StripePaymentMethodInput } from '@/components/payment/stripe-payment-method-input';
import { StripePaymentVerification } from '@/components/payment/stripe-payment-verification';
import { Button, Modal } from '@/components/ui/design-system';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useIsAdminOrManager } from '@/hooks/api/team';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

const isTestingEnvironment = (() => {
  const apiUrl = import.meta.env.VITE_API_ALLO_URL || '';
  return apiUrl.includes('localhost') || apiUrl.includes('api-dev');
})();

const BrandRouteContent = () => {
  const navigate = useNavigate();
  const {
    brand,
    isLoading: isLoadingBrand,
    isError: isBrandsError,
    refetchBrands,
  } = useComplianceLayout();
  const { isAdminOrManager, isLoading: isLoadingRole } = useIsAdminOrManager();
  const [isOtpOpen, setIsOtpOpen] = useState(false);

  useEffect(() => {
    if (isLoadingRole) {
      return;
    }
    if (!isAdminOrManager) {
      navigate({ to: '/settings/compliance', replace: true });
    }
  }, [isAdminOrManager, isLoadingRole, navigate]);

  const { requestDelete, confirmDelete, cancelDelete, isConfirmOpen, isDeleting } = useDeleteBrand({
    onSuccess: () => navigate({ to: '/settings/compliance' }),
  });

  const brandId: string | undefined = brand?.id;
  const hasDraftCampaign = !!brand?.campaigns?.[0]?.id;

  const { create: createDraftBrand, isCreating: isCreatingDraft } = useCreateDraftBrand({
    onSuccess: () => {
      refetchBrands();
    },
  });

  const draftCreateRequestedRef = useRef(false);
  useEffect(() => {
    // Once we've seen a brand on this mount, never auto-create another one.
    // This prevents the effect from re-creating a draft right after the user
    // deletes their brand (before the route unmounts via navigate).
    if (brand) {
      draftCreateRequestedRef.current = true;
      return;
    }
    if (
      !isAdminOrManager ||
      isLoadingBrand ||
      isBrandsError ||
      isCreatingDraft ||
      draftCreateRequestedRef.current
    ) {
      return;
    }
    draftCreateRequestedRef.current = true;
    createDraftBrand();
  }, [isAdminOrManager, isLoadingBrand, isBrandsError, brand, isCreatingDraft, createDraftBrand]);

  const { updateAsync: updateBrandDraft } = useUpdateBrandDraft(brandId);

  const navigateAfterSubmit = (b: BrandWithCampaignsResponse | null | undefined) => {
    if (b?.campaigns?.[0]?.id) {
      navigate({ to: '/settings/compliance/campaign' });
      return;
    }
    navigate({ to: '/settings/compliance' });
  };

  const {
    submit: submitBrand,
    isLoading: isSubmitting,
    error,
    mismatchErrors,
    regenerateKeyCounter,
    verificationState,
    clientSecret,
    pendingIdempotencyKey,
    handlePaymentMethodSuccess,
    handleVerificationSuccess,
    handleVerificationError,
  } = useSubmitBrand(brandId, {
    onSuccess: async (b) => {
      await refetchBrands();
      if (b?.entity_type === 'SOLE_PROPRIETOR' && b?.id && b?.otp_status !== 'VERIFIED') {
        setIsOtpOpen(true);
        return;
      }
      navigateAfterSubmit(b);
    },
  });

  const handleFormSubmit = async (data: UpdateBrandRequest, idempotencyKey: string) => {
    if (!brandId) {
      return;
    }
    try {
      await updateBrandDraft(data);
    } catch {
      return;
    }
    submitBrand({}, idempotencyKey);
  };

  const handleFieldBlurSave = (partial: UpdateBrandRequest) => {
    if (!brandId) {
      return;
    }
    updateBrandDraft(partial).catch(() => {});
  };

  return (
    <>
      <StepIntro
        step={1}
        title="Create your brand"
        subtitle="A registered brand is required to send SMS."
      />
      <SoleProprietorOtpAlert brand={brand} onVerifyClick={() => setIsOtpOpen(true)} />
      {isLoadingBrand || (!brand && !isBrandsError && isCreatingDraft) ? (
        <ComplianceSkeleton />
      ) : isBrandsError && !brand ? (
        <p className="py-8 text-body-13 text-primary-80">
          We couldn't load your brand. Please try again.
        </p>
      ) : (
        <BrandForm
          initialData={brand}
          onSubmit={handleFormSubmit}
          onFieldBlurSave={handleFieldBlurSave}
          onUploaded={refetchBrands}
          onNext={() => {
            if (hasDraftCampaign) {
              navigate({ to: '/settings/compliance/campaign' });
              return;
            }
            navigate({ to: '/settings/compliance' });
          }}
          onDelete={isTestingEnvironment && brand ? () => requestDelete(brand.id) : undefined}
          isLoading={isSubmitting}
          error={error}
          mismatchErrors={mismatchErrors}
          regenerateKey={regenerateKeyCounter}
        />
      )}

      <Modal
        isOpen={isConfirmOpen}
        variant="dialog"
        title="Delete Brand"
        description="This will permanently delete this brand and all associated campaigns. This action cannot be undone."
        onClose={cancelDelete}
        actions={[
          <Button
            key="cancel"
            variant="outline"
            size="sm"
            onClick={cancelDelete}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            variant="destructive"
            size="sm"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>,
        ]}
      />

      <Dialog
        open={
          (verificationState === 'payment-method' || verificationState === 'verification') &&
          !!clientSecret
        }
        onOpenChange={(open) => {
          if (!open) {
            handleVerificationError();
          }
        }}
      >
        <DialogContent
          aria-description="Payment Dialog"
          className="w-[382px] max-h-screen gap-8 overflow-auto rounded-lg p-0 py-6"
          closeButtonStyle="opacity-100 hover:bg-primary-alpha-5 p-1 rounded-md group top-2 right-2"
          closeIconStyle="stroke-primary/50 stroke-2.5 size-4 group-hover:stroke-primary"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {verificationState === 'payment-method' ? 'Add Payment Method' : 'Payment Verification'}
          </DialogTitle>

          {verificationState === 'payment-method' && clientSecret && (
            <StripeElements clientSecret={clientSecret}>
              <StripePaymentMethodInput
                idempotencyKey={pendingIdempotencyKey || crypto.randomUUID()}
                onSuccess={handlePaymentMethodSuccess}
                onError={handleVerificationError}
                meta={{ mode: 'compliance' }}
              />
            </StripeElements>
          )}

          {verificationState === 'verification' && clientSecret && (
            <StripeElements>
              <StripePaymentVerification
                onSuccess={handleVerificationSuccess}
                onError={handleVerificationError}
                clientSecret={clientSecret}
              />
            </StripeElements>
          )}
        </DialogContent>
      </Dialog>

      {brand?.id && brand.entity_type === 'SOLE_PROPRIETOR' && (
        <SoleProprietorOtpDialog
          open={isOtpOpen}
          brandId={brand.id}
          mobilePhone={brand.mobile_phone}
          otpStatus={brand.otp_status}
          onVerified={(verifiedBrand) => {
            setIsOtpOpen(false);
            refetchBrands();
            // OTP verification is the last gate before the campaign step. If a
            // draft campaign already exists for this brand, advance the user.
            if (verifiedBrand?.campaigns?.[0]?.id) {
              navigate({ to: '/settings/compliance/campaign' });
            }
          }}
          onClose={() => {
            setIsOtpOpen(false);
            refetchBrands();
          }}
        />
      )}
    </>
  );
};

const RouteComponent = () => {
  return (
    <ComplianceLayout currentStep={1}>
      <BrandRouteContent />
    </ComplianceLayout>
  );
};

export const Route = createFileRoute('/_app/settings/compliance/brand')({
  component: RouteComponent,
  onEnter: () => analytics.track(ANALYTICS_EVENTS.COMPLIANCE.BRAND_CREATION_STARTED),
});
